// Warstwa tłumaczenia nad DOM-em (wersja angielska). Komponenty zostają po polsku;
// przy EN obserwator podmienia teksty i atrybuty (title, placeholder, aria-label, alt)
// wg słownika — także teksty z API, wykresy SVG, druk PDF i eksport PNG (klon DOM-u).
//
// Zasady:
// • oryginał każdego węzła siedzi w WeakMap — zmiana od Reacta (inna wartość niż nasza)
//   to nowy oryginał, nasza własna mutacja jest rozpoznawana i pomijana (bez pętli);
// • element z samymi dziećmi-tekstami („Mecz ", "5", " z ", "8") tłumaczymy RAZEM jako
//   wzorzec, potem pierwsze dziecko dostaje całość, reszta pusty tekst;
// • poddrzewa `translate="no"` / `.notranslate`, pola edycji i contenteditable — nigdy
//   (komentarze sztabu, notatki, wszystko wpisywane ręcznie).
import type { Translator } from './translate'

const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'INPUT', 'CODE', 'PRE', 'svg:style'])
const ATTRS = ['title', 'placeholder', 'aria-label', 'alt'] as const
const NO_SELECTOR = '[translate="no"], .notranslate, [contenteditable="true"], [contenteditable=""]'

interface Rec { src: string; out: string }

export function startDomTranslator(t: Translator, root: Node = document.documentElement): () => void {
  const texts = new WeakMap<Text, Rec>()
  const attrs = new WeakMap<Element, Map<string, Rec>>()

  const excluded = (el: Element | null): boolean => {
    if (!el) return true
    if (SKIP_TAGS.has(el.tagName)) return true
    return el.closest(NO_SELECTOR) !== null
  }

  const srcOf = (n: Text): string => {
    const v = n.nodeValue ?? ''
    const r = texts.get(n)
    return r && r.out === v ? r.src : v
  }

  const setText = (n: Text, src: string, out: string) => {
    texts.set(n, { src, out })
    if (n.nodeValue !== out) n.nodeValue = out
  }

  // Element z samymi tekstami jako dziećmi (> 1) — spróbuj całości jako wzorca.
  const tryGroup = (parent: Element): boolean => {
    const kids = parent.childNodes
    if (kids.length < 2 || kids.length > 12) return false
    const nodes: Text[] = []
    for (const k of kids) {
      if (k.nodeType !== Node.TEXT_NODE) return false
      nodes.push(k as Text)
    }
    const srcs = nodes.map(srcOf)
    const whole = srcs.join('')
    const out = t.translate(whole)
    if (out == null) return false
    nodes.forEach((n, i) => setText(n, srcs[i], i === 0 ? out : ''))
    return true
  }

  const processText = (n: Text) => {
    const v = n.nodeValue ?? ''
    const r = texts.get(n)
    if (r && r.out === v) return // nasza własna wartość
    const parent = n.parentElement
    if (!parent || excluded(parent)) return
    if (tryGroup(parent)) return
    const out = t.translate(v)
    if (out != null && out !== v) setText(n, v, out)
    else texts.delete(n)
  }

  const processAttr = (el: Element, name: string) => {
    const v = el.getAttribute(name)
    let m = attrs.get(el)
    const r = m?.get(name)
    if (v == null || (r && r.out === v)) return
    if (excluded(el) && el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA') return
    if ((el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') && name !== 'placeholder' && name !== 'title' && name !== 'aria-label') return
    const out = t.translate(v)
    if (out == null || out === v) return
    if (!m) attrs.set(el, (m = new Map()))
    m.set(name, { src: v, out })
    el.setAttribute(name, out)
  }

  const processElementAttrs = (el: Element) => {
    for (const a of ATTRS) if (el.hasAttribute(a)) processAttr(el, a)
  }

  const processTree = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) { processText(node as Text); return }
    if (node.nodeType !== Node.ELEMENT_NODE) return
    const el = node as Element
    if (SKIP_TAGS.has(el.tagName)) {
      // Pola edycji: tylko placeholder/title (wartości wpisuje człowiek).
      if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') processElementAttrs(el)
      return
    }
    if (el.matches(NO_SELECTOR)) return
    if (excluded(el.parentElement) && el !== root && el.parentElement) return
    processElementAttrs(el)
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
      acceptNode(n) {
        if (n.nodeType === Node.ELEMENT_NODE) {
          const e = n as Element
          if (SKIP_TAGS.has(e.tagName)) {
            if (e.tagName === 'INPUT' || e.tagName === 'TEXTAREA') processElementAttrs(e)
            return NodeFilter.FILTER_REJECT
          }
          if (e.matches(NO_SELECTOR)) return NodeFilter.FILTER_REJECT
        }
        return NodeFilter.FILTER_ACCEPT
      },
    })
    const textNodes: Text[] = []
    for (let n = walker.nextNode(); n; n = walker.nextNode()) {
      if (n.nodeType === Node.TEXT_NODE) textNodes.push(n as Text)
      else processElementAttrs(n as Element)
    }
    for (const tn of textNodes) processText(tn)
  }

  const observer = new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.type === 'characterData') processText(m.target as Text)
      else if (m.type === 'childList') {
        m.addedNodes.forEach(processTree)
        // Usunięcie/dodanie rodzeństwa zmienia grupę tekstów rodzica.
        if (m.target.nodeType === Node.ELEMENT_NODE && m.removedNodes.length) {
          const el = m.target as Element
          if (!excluded(el)) tryGroup(el)
        }
      } else if (m.type === 'attributes' && m.attributeName) processAttr(m.target as Element, m.attributeName)
    }
  })
  processTree(root)
  observer.observe(root, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: [...ATTRS],
  })
  return () => observer.disconnect()
}
